import { AgreementFields, AgreementDataRow } from "./AgreementDataRow";
import {
  groupFieldsByGroup,
  getGroupTitleKey,
  FileMetadata,
} from "../../utils";
import { useTranslation } from "react-i18next";

interface AgreementGroupedDataSectionProps {
  title: string;
  fields: AgreementFields;
  draftData: Record<string, unknown>;
  confirmedData: Record<string, unknown>;
  translationPrefix: string;
  attachments?: Record<string, FileMetadata[]>;
}

interface GroupTableProps {
  groupKey: string;
  groupFields: AgreementFields;
  isFirstGroup: boolean;
  draftData: Record<string, unknown>;
  confirmedData: Record<string, unknown>;
  translationPrefix: string;
  attachments?: Record<string, FileMetadata[]>;
}

const GroupTable = ({
  groupKey,
  groupFields,
  isFirstGroup,
  draftData,
  confirmedData,
  translationPrefix,
  attachments,
}: GroupTableProps) => {
  const { t } = useTranslation();

  return (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-1/4 pb-1.5 border-b-2 border-gray-300 text-left">
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                {t(getGroupTitleKey(groupKey))}
              </h3>
            </th>
            <th className="w-1/4 pb-1.5 border-b-2 border-gray-300 text-left">
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide opacity-0">
                -
              </h3>
            </th>
            <th className="w-1/4 pb-1.5 border-b-2 border-gray-300 text-left">
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                {isFirstGroup ? (
                  t("agreements.common.confirmedData")
                ) : (
                  <>
                    <span className="lg:hidden">
                      {t(getGroupTitleKey(groupKey))}
                    </span>
                    <span className="hidden lg:inline">&nbsp;</span>
                  </>
                )}
              </h3>
            </th>
            <th className="w-1/4 pb-1.5 border-b-2 border-gray-300 text-left">
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide opacity-0">
                -
              </h3>
            </th>
          </tr>
        </thead>
        <tbody>
          {groupFields.map((field, index) => (
            <AgreementDataRow
              key={field.key}
              field={field}
              draftValue={draftData?.[field.key]}
              confirmedValue={confirmedData?.[field.key]}
              translationPrefix={translationPrefix}
              attachments={attachments}
              draftData={draftData}
              confirmedData={confirmedData}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AgreementGroupedDataSection = ({
  title,
  fields,
  draftData,
  confirmedData,
  translationPrefix,
  attachments,
}: AgreementGroupedDataSectionProps) => {
  const groupedFields = groupFieldsByGroup(fields);

  return (
    <div className="space-y-3">
      {title}
      <div className="space-y-3">
        {Object.entries(groupedFields).map(([groupKey, groupFields], index) => (
          <GroupTable
            key={groupKey}
            groupKey={groupKey}
            groupFields={groupFields}
            isFirstGroup={index === 0}
            draftData={draftData}
            confirmedData={confirmedData}
            translationPrefix={translationPrefix}
            attachments={attachments}
          />
        ))}
      </div>
    </div>
  );
};
